/**
 * SentenceBuildExercise — arrange scrambled word chips into the correct sentence.
 *
 * Layout:
 *   1. "Arrange the sentence" label + definition shown as a small hint (the clue)
 *   2. Sentence area — fills as the user taps chips; tap again to return
 *   3. Word chip pool — scrambled chips; the term is shown as "________" (blank chip)
 *   4. Submit button
 *
 * The blank chip ("________") represents the word being learned.  The parent passes
 * `term` so we can substitute it back into the answer before calling onSubmit —
 * the backend answer-check sees the real word, not the placeholder.
 *
 * On correct → green feedback, auto-advance handled by parent (no action needed here)
 * On wrong   → red feedback + correct sentence revealed; auto-advance by parent after 2s
 * No retype required — this format uses the same no-retype path as multiple_choice.
 */
import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import MasteryBadge from './MasteryBadge'
import type { LearningAnswerResult } from '../../types'
import { useLocale } from '../../i18n/context'

export type SentencePhase = 'idle' | 'correct' | 'wrong'

interface SelectedChip {
  word: string
  /** Index in the original `words` array — used for deduplication (e.g. two "the"s). */
  poolIdx: number
}

const BLANK_TOKEN = '________'

interface Props {
  /** Definition text shown as a small hint/clue above the sentence area. */
  definition: string
  /**
   * Scrambled word chips to display in the pool.
   * The term is represented by "________" — a blank chip the learner places
   * to show they know where the word belongs.
   */
  words: string[]
  /** The canonical correct sentence (real term, not blank) — revealed on wrong answer. */
  correctSentence: string
  /**
   * The word being learned (the term).  Used to substitute the blank chip back
   * into the answer string before it is sent to the backend.
   */
  term: string
  phase: SentencePhase
  onSubmit: (answer: string) => void
  disabled?: boolean
  masteryLevel?: number
  lastResult?: LearningAnswerResult | null
}

export default function SentenceBuildExercise({
  definition,
  words,
  correctSentence,
  term,
  phase,
  onSubmit,
  disabled,
  masteryLevel,
  lastResult,
}: Props) {
  const { tr } = useLocale()
  const lrn = tr.learn

  // selected: ordered list of chips the user has tapped into the sentence area
  const [selected, setSelected] = useState<SelectedChip[]>([])
  // poolUsed[i] = true when chip at index i is in the sentence area
  const [poolUsed, setPoolUsed] = useState<boolean[]>(() => new Array(words.length).fill(false))

  // Reset state whenever a new set of words arrives (new card)
  useEffect(() => {
    setSelected([])
    setPoolUsed(new Array(words.length).fill(false))
  }, [words])

  const handlePickFromPool = useCallback(
    (idx: number) => {
      if (phase !== 'idle' || disabled || poolUsed[idx]) return
      setPoolUsed((prev) => {
        const next = [...prev]
        next[idx] = true
        return next
      })
      setSelected((prev) => [...prev, { word: words[idx], poolIdx: idx }])
    },
    [phase, disabled, poolUsed, words],
  )

  const handleReturnToPool = useCallback(
    (selectedIdx: number) => {
      if (phase !== 'idle' || disabled) return
      const chip = selected[selectedIdx]
      setPoolUsed((prev) => {
        const next = [...prev]
        next[chip.poolIdx] = false
        return next
      })
      setSelected((prev) => prev.filter((_, i) => i !== selectedIdx))
    },
    [phase, disabled, selected],
  )

  const handleSubmit = () => {
    if (!selected.length || disabled || phase !== 'idle') return
    // Replace the blank chip with the real term before sending to the backend.
    // The server checks against the canonical sentence (which has the real word).
    const rawAnswer = selected.map((c) => c.word).join(' ')
    const answer = term ? rawAnswer.replace(BLANK_TOKEN, term) : rawAnswer
    onSubmit(answer)
  }

  // Colour tokens for the sentence area and chips
  const areaColor = phase === 'correct' ? 'var(--success)' : phase === 'wrong' ? 'var(--danger)' : 'var(--border)'
  const areaBg =
    phase === 'correct'
      ? 'rgba(39, 174, 96, 0.06)'
      : phase === 'wrong'
      ? 'var(--danger-soft)'
      : 'var(--bg-surface)'

  const masteryChange =
    lastResult && phase !== 'idle' ? lastResult.mastery_change : null
  const isMastered = phase === 'correct' && lastResult?.is_correct ? lastResult.is_mastered : false

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 640,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* ── Exercise header ───────────────────────────────────────────────────── */}
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-flashcard)',
          padding: '20px 20px 18px',
          position: 'relative',
        }}
      >
        {/* Mode label */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--accent)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            textAlign: 'center',
            marginBottom: 14,
          }}
        >
          {lrn.arrangeSentence}
        </div>

        {/* Definition — shown as a compact hint, NOT the sentence to arrange */}
        {definition && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                flexShrink: 0,
              }}
            >
              {lrn.hint}
            </span>
            <span
              style={{
                fontSize: 15,
                fontFamily: 'var(--font-content)',
                color: 'var(--text-secondary)',
                textAlign: 'center',
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}
            >
              {definition}
            </span>
          </div>
        )}
      </div>

      {/* ── Answer area ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Mastery pip strip + change indicator — magnitude model, Wave 4 §5 */}
        {masteryLevel != null && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 24 }}>
            <MasteryBadge level={masteryLevel} size="dot" />
            {phase === 'correct' && masteryChange != null && masteryChange !== 0 && (
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
                {lrn.masteryChange(masteryChange, isMastered)}
              </span>
            )}
          </div>
        )}

        {/* Feedback row */}
        <AnimatePresence mode="wait">
          {phase === 'correct' && (
            <motion.div
              key="correct-msg"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)' }}>
                {lrn.correctExcl}
              </span>
            </motion.div>
          )}
          {phase === 'wrong' && (
            <motion.div
              key="wrong-msg"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}
            >
              <span style={{ fontSize: 14, color: 'var(--danger)', fontWeight: 500 }}>
                {lrn.incorrectExcl}
              </span>
              {correctSentence && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontFamily: 'var(--font-content)',
                    color: 'var(--text-secondary)',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lrn.correctLabel} </span>
                  {correctSentence}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sentence area — chips appear here as the user selects them */}
        <div
          style={{
            minHeight: 60,
            borderRadius: 12,
            border: `2px ${phase === 'idle' ? 'dashed' : 'solid'} ${areaColor}`,
            backgroundColor: areaBg,
            padding: '10px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'flex-start',
            alignContent: 'flex-start',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
        >
          {selected.length === 0 ? (
            <span
              style={{
                fontSize: 14,
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-ui)',
                userSelect: 'none',
                lineHeight: '36px',
              }}
            >
              {lrn.tapWordsToBuild}
            </span>
          ) : (
            selected.map((chip, i) => (
              <WordChip
                key={`sel-${chip.poolIdx}`}
                word={chip.word}
                onClick={() => handleReturnToPool(i)}
                variant={phase === 'correct' ? 'correct' : phase === 'wrong' ? 'wrong' : 'selected'}
                disabled={phase !== 'idle' || disabled}
              />
            ))
          )}
        </div>

        {/* Pool — available chips (used chips are hidden, not removed, to preserve layout stability) */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            minHeight: 44,
          }}
        >
          {words.map((word, i) => (
            !poolUsed[i] && (
              <WordChip
                key={`pool-${i}`}
                word={word}
                onClick={() => handlePickFromPool(i)}
                variant="idle"
                disabled={phase !== 'idle' || disabled}
              />
            )
          ))}
        </div>

        {/* Submit button */}
        {phase === 'idle' && (
          <button
            onClick={handleSubmit}
            disabled={!selected.length || !!disabled}
            style={{
              padding: '14px 24px',
              borderRadius: 50,
              border: 'none',
              background: selected.length && !disabled ? 'var(--accent-fill)' : 'var(--bg-secondary)',
              color: selected.length && !disabled ? 'var(--text-on-accent)' : 'var(--text-secondary)',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: selected.length && !disabled ? 'pointer' : 'default',
              transition: 'background 0.2s, color 0.2s',
              width: '100%',
            }}
          >
            {disabled ? lrn.checking : lrn.submitArrow}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Word chip ─────────────────────────────────────────────────────────────────

type ChipVariant = 'idle' | 'selected' | 'correct' | 'wrong'

function WordChip({
  word,
  onClick,
  variant,
  disabled,
}: {
  word: string
  onClick: () => void
  variant: ChipVariant
  disabled?: boolean
}) {
  const isBlank = word === BLANK_TOKEN
  const isInteractive = !disabled && (variant === 'idle' || variant === 'selected')

  // Blank chips get a dashed accent border and italic style so users
  // can immediately identify "this is the word I'm learning".
  const borderColor =
    variant === 'correct'
      ? 'var(--success)'
      : variant === 'wrong'
      ? 'var(--danger)'
      : isBlank && variant === 'idle'
      ? 'var(--accent)'
      : variant === 'selected'
      ? 'var(--accent)'
      : 'var(--border)'

  const borderStyle = isBlank && variant === 'idle' ? 'dashed' : 'solid'

  const bgColor =
    variant === 'correct'
      ? 'rgba(39, 174, 96, 0.1)'
      : variant === 'wrong'
      ? 'var(--danger-soft)'
      : isBlank && variant === 'idle'
      ? 'var(--accent-soft)'
      : variant === 'selected'
      ? 'var(--accent-soft)'
      : 'var(--bg-surface)'

  const textColor =
    variant === 'correct'
      ? 'var(--success)'
      : variant === 'wrong'
      ? 'var(--danger)'
      : isBlank
      ? 'var(--accent)'
      : variant === 'selected'
      ? 'var(--accent)'
      : 'var(--text-primary)'

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.88 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.88 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      onClick={isInteractive ? onClick : undefined}
      style={{
        padding: '8px 14px',
        borderRadius: 8,
        border: `1.5px ${borderStyle} ${borderColor}`,
        backgroundColor: bgColor,
        color: textColor,
        fontSize: 16,
        fontFamily: 'var(--font-content)',
        fontWeight: 400,
        cursor: isInteractive ? 'pointer' : 'default',
        minHeight: 40,
        transition: 'border-color 0.15s, background-color 0.15s, color 0.15s, transform 0.1s',
        outline: 'none',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        if (!isInteractive) return
        ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.04)'
      }}
      onMouseLeave={(e) => {
        if (!isInteractive) return
        ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
      }}
    >
      {word}
    </motion.button>
  )
}
